import { useDataProvider, useGetList } from 'ra-core'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@mui/material'
import { useQueryClient } from 'react-query'
import { useCustomChartContext } from './CustomChartState'
import { CustomChartPreview } from './CustomChartPreview'
import { ChartType, Selectable, colorSchemes } from './types'
import { Card } from '../Card'
import { SectionTitle } from '../Title'
import { Button } from '../common/Button'
import GenericVirtualizedAutoComplete from '../ValueEditors/GenericVirtualizedAutoComplete'
import { chartTypes, dataTypes, isMultiSeries } from './helpers'

const MIN_ITEMS = 1
const MAX_ITEMS = 10

const paramLabels = {
  tags: 'Tags',
  vendors: 'Vendors',
  tag_categories: 'Tag Categories',
}

export const ItemPickerPreview = () => {
  const {
    state: { items, dataType, selectedTagCategory },
    dispatch,
  } = useCustomChartContext()
  const paramType = useMemo(
    () => (dataTypes.find((d) => d.id === dataType) ?? dataTypes[0]).params,
    [dataType]
  )
  const requiresTagCategory = useMemo(() => {
    if (paramType !== 'tags') return false
    if (isMultiSeries(dataType)) return true
    return dataType === 'total-spend-by-tag-category'
  }, [dataType, paramType])
  const baseItems = useMemo(() => items ?? [], [items])
  useCallback(
    (item: Selectable) => {
      const newItems = baseItems.some((i) => i.id === item.id)
        ? baseItems.filter((i) => i.id !== item.id)
        : [...baseItems, item]
      dispatch({ type: 'SET_ITEMS', payload: { items: newItems } })
    },
    [baseItems, dispatch]
  )
  if (!items || items?.length === 0) return <div />

  return (
    <div className="space-y-2 pl-3 text-indigo-700">
      {requiresTagCategory && selectedTagCategory ? (
        <div>
          Tag Category:{' '}
          <span className="font-light">{selectedTagCategory.name}</span>
        </div>
      ) : null}
      <div>
        {items.length} item{items.length > 1 ? 's' : ''} selected
      </div>
    </div>
  )
}

export const ItemPicker = () => {
  const { t } = useTranslation()
  const {
    state: {
      dataType,
      items,
      selectedTagCategory,
      name,
      description,
      chartType,
      colorScheme,
    },
    dispatch,
  } = useCustomChartContext()
  const queryClient = useQueryClient()
  const dp = useDataProvider()
  const [openTooltip, setOpenTooltip] = useState(false)
  const appliedItems = useMemo(() => items ?? [], [items])
  const [draftItems, setDraftItems] = useState<Selectable[]>(appliedItems)
  const PREVIEW_DEBOUNCE_MS = 1000

  const paramType = useMemo(
    () => (dataTypes.find((d) => d.id === dataType) ?? dataTypes[0]).params,
    [dataType]
  )

  const requiresTagCategory = useMemo(() => {
    if (paramType !== 'tags') return false
    if (isMultiSeries(dataType)) return true
    return dataType === 'total-spend-by-tag-category'
  }, [dataType, paramType])

  const shouldFetchItems = useMemo(() => {
    if (paramType !== 'tags') return true
    if (!requiresTagCategory) return true
    return !!selectedTagCategory
  }, [paramType, requiresTagCategory, selectedTagCategory])

  const { data: tagCategories, isLoading: isLoadingCategories } =
    useGetList<Selectable>('tag_categories', {
      filter: null,
      sort: { field: 'name', order: 'asc' },
      pagination: { page: 1, perPage: 1000 },
    })

  useEffect(() => {
    setDraftItems(appliedItems)
  }, [appliedItems])

  const baseItems = useMemo(() => draftItems ?? [], [draftItems])
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      dispatch({ type: 'SET_ITEMS', payload: { items: baseItems } })
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [baseItems, dispatch])

  const onSelect = useCallback(
    (item: Selectable) => {
      const newItems = baseItems.some((i) => i.id === item.id)
        ? baseItems.filter((i) => i.id !== item.id)
        : [...baseItems, item]
      setDraftItems(newItems)
    },
    [baseItems]
  )

  const onSelectCategory = useCallback(
    (category: Selectable | null) => {
      dispatch({ type: 'SET_TAG_CATEGORY', payload: { tagCategory: category } })
    },
    [dispatch]
  )

  const availableChartTypes = useMemo(
    () =>
      isMultiSeries(dataType)
        ? chartTypes
        : chartTypes.filter((ct) => ct.id !== 'line'),
    [dataType]
  )

  useEffect(() => {
    const nextChartType = availableChartTypes[0]?.id
    if (!nextChartType) return
    if (!chartType || !availableChartTypes.some((ct) => ct.id === chartType)) {
      dispatch({
        type: 'SET_CHART_TYPE',
        payload: { chartType: nextChartType },
      })
    }
  }, [availableChartTypes, chartType, dispatch])

  useEffect(() => {
    const defaultScheme = Object.keys(colorSchemes)[0]
    if (defaultScheme && !colorScheme) {
      dispatch({
        type: 'SET_COLOR_SCHEME',
        payload: { colorScheme: defaultScheme },
      })
    }
  }, [colorScheme, dispatch])

  const canCreate = useMemo(() => {
    if (!name) return false
    if (!description) return false
    if (!dataType) return false
    if (!chartType) return false
    if (!baseItems.length) return false
    if (!colorScheme) return false
    return baseItems.length >= MIN_ITEMS && baseItems.length <= MAX_ITEMS
  }, [baseItems.length, chartType, colorScheme, dataType, description, name])

  const handleTooltipClose = () => {
    setOpenTooltip(false)
  }

  const handleTooltipOpen = () => {
    if (!canCreate) {
      setOpenTooltip(true)
    }
  }

  const selectChartType = (ct: ChartType) => {
    dispatch({ type: 'SET_CHART_TYPE', payload: { chartType: ct } })
  }

  const createWidget = async () => {
    if (!dataType || !chartType || !colorScheme || !baseItems.length) return
    dp.addWidget(name, description, true, true, {
      dataType,
      chartType,
      items: baseItems.map((i) => ({ id: i.id, name: i.name })),
      colorScheme,
    }).then(() => {
      dispatch({ type: 'RESET' })
      queryClient.invalidateQueries('widgets')
    })
  }

  const onAutocompleteChange = useCallback(
    (ids: number[]) => {
      if (!ids.length) {
        setDraftItems([])
        return
      }
      if (paramType === 'tag_categories' && tagCategories?.length) {
        const newItems = tagCategories.filter((category) =>
          ids.includes(category.id)
        )
        setDraftItems(newItems)
        return
      }
      dp.getMany(paramType, { ids }).then((response: any) => {
        const newItems = response?.data ?? []
        setDraftItems(newItems)
      })
    },
    [dp, paramType, tagCategories]
  )

  const previewChart = {
    chartType,
    dataType,
    items: appliedItems,
    colorScheme,
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto pr-1 space-y-6">
        <div className="flex gap-8 flex-wrap">
          <div className="space-y-1">
            <h3 className="text-sm font-medium min-w-20">
              {t('custom_widgets.chart_type')}
            </h3>
            <div className="inline-flex rounded-lg border border-gray-300 shadow-sm overflow-hidden divide-x">
              {availableChartTypes.map((ct) => (
                <button
                  type="button"
                  className={classNames(
                    'py-2.5 px-3.5 active hover:bg-gray-50 flex-1',
                    {
                      'text-indigo-700 bg-indigo-50': ct.id === chartType,
                    }
                  )}
                  key={ct.id}
                  onClick={() => selectChartType(ct.id)}
                >
                  {ct.icon}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1 flex-1 min-w-[260px]">
            <h3 className="text-sm font-medium">
              {t('custom_widgets.color_scheme')}
            </h3>
            <div className="flex rounded-lg border border-gray-300 shadow-sm overflow-hidden h-11 divide-x">
              {Object.keys(colorSchemes).map((key) => {
                const cs = colorSchemes[key]
                if (!cs) return null
                return (
                  <button
                    className={classNames(
                      'flex-1 px-3 active hover:opacity-100 hover:bg-gray-50',
                      {
                        'bg-indigo-50': key === colorScheme,
                        'opacity-60': key !== colorScheme,
                      }
                    )}
                    key={key}
                    onClick={() =>
                      dispatch({
                        type: 'SET_COLOR_SCHEME',
                        payload: { colorScheme: key },
                      })
                    }
                  >
                    <div className="flex rounded-sm overflow-hidden flex-wrap">
                      {cs.map((color) => (
                        <div
                          className="w-4 h-3.5"
                          key={color}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="font-medium text-lg">
              Select {paramLabels[paramType].toLowerCase()} for visualisation
            </h3>
            <div className="flex items-center flex-wrap gap-3">
              {requiresTagCategory && (
                <select
                  className="h-10 px-3 rounded-md border border-gray-300 shadow-sm text-sm min-w-[220px] bg-white"
                  value={selectedTagCategory?.id ?? ''}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      onSelectCategory(null)
                      return
                    }
                    const nextId = Number(e.target.value)
                    const nextCategory =
                      tagCategories?.find((c) => c.id === nextId) ?? null
                    onSelectCategory(nextCategory)
                  }}
                  disabled={isLoadingCategories}
                >
                  <option value="">
                    {isLoadingCategories
                      ? 'Loading tag categories...'
                      : 'Select tag category'}
                  </option>
                  {tagCategories?.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              )}
              <div className="min-w-[300px] flex-1 flex items-center h-10">
                {!shouldFetchItems ? (
                  <div className="h-10 flex items-center text-sm text-gray-500">
                    Please select a tag category first
                  </div>
                ) : (
                  <GenericVirtualizedAutoComplete
                    key={`${paramType}-${selectedTagCategory?.id ?? 'none'}`}
                    value={baseItems.map((item) => item.id)}
                    label={paramLabels[paramType]}
                    filter={
                      paramType === 'tags' && selectedTagCategory
                        ? { tag_category_id: selectedTagCategory.id }
                        : null
                    }
                    handleOnChange={onAutocompleteChange}
                    dpFetchValue={paramType}
                    variant="outlined"
                    size="small"
                    maxSelected={MAX_ITEMS}
                    showSelectAll={false}
                    selectedOptions={
                      paramType === 'tag_categories'
                        ? tagCategories ?? []
                        : undefined
                    }
                  />
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm font-medium">
              <span>Selected {paramLabels[paramType]}</span>
              <span className="text-xs text-gray-500">
                {baseItems.length}/{MAX_ITEMS}
              </span>
            </div>
            <div className="h-28 max-h-28 overflow-y-auto rounded-lg border border-gray-200 p-2 bg-white">
              {baseItems.length ? (
                <div className="flex flex-wrap gap-2 items-center">
                  {baseItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => onSelect(item)}
                      className="border text-indigo-500 hover:bg-white bg-indigo-50/40 shadow-sm text-sm rounded-lg px-2 py-1.5 font-medium flex gap-1.5 items-center active border-gray-300"
                      type="button"
                      title={item.name}
                    >
                      <span className="flex-1 line-clamp-2 text-left">
                        {item.name}
                      </span>
                      <XMarkIcon className="h-3.5" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 h-full flex items-center">
                  No items selected
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="text-sm font-medium">{t('custom_widgets.preview')}</h3>
          <Card className="hover:scale-100 hover:shadow-none">
            <SectionTitle title={name ?? '-'} subtitle={description ?? '-'} />
            <CustomChartPreview chart={previewChart} heightClass="h-[295px]" />
          </Card>
        </div>
      </div>

      <div className="pt-4 space-y-2">
        <div className="text-sm font-medium">
          Enter name and description for Widget
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 flex items-center gap-3">
            <input
              className="p-2.5 rounded-lg border border-gray-300 shadow-sm flex-1 min-w-[200px] text-sm"
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) =>
                dispatch({
                  type: 'SET_NAME',
                  payload: { name: e.target.value },
                })
              }
            />
            <input
              className="p-2.5 rounded-lg border border-gray-300 shadow-sm flex-[2] min-w-[240px] text-sm"
              type="text"
              placeholder="Description"
              value={description}
              onChange={(e) =>
                dispatch({
                  type: 'SET_DESCRIPTION',
                  payload: { description: e.target.value },
                })
              }
            />
          </div>
          <Tooltip
            title={`Complete all fields and select between ${MIN_ITEMS} and ${MAX_ITEMS} items.`}
            placement="top-end"
            open={openTooltip}
            onClose={handleTooltipClose}
            onOpen={handleTooltipOpen}
          >
            <div className="relative group">
              <Button disabled={!canCreate} onClick={() => createWidget()}>
                <span className="flex items-center gap-2">
                  <span>{t('custom_widgets.create')}</span>
                  <ChevronRightIcon className="h-3" />
                </span>
              </Button>
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
